import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Collapse,
  Link,
  TextField,
  Box,
  Stack,
  Button,
  Typography,
  Avatar,
} from "@mui/material";
import BrandAssetUpload from "./BrandAssetUpload";
import type { BrandAsset } from "../api/brandAssets";

type BrandingData = {
  logo_url: string | null;
  primary_colour: string | null;
  secondary_colour: string | null;
  booking_background_colour?: string | null;
  booking_background_image_url?: string | null;
};

type BrandingCardProps = {
  initialBranding: BrandingData;
  onSave: (updates: Partial<BrandingData>) => Promise<void>;
  assetByType?: Partial<Record<BrandAsset["asset_type"], BrandAsset>>;
  onUpload?: (assetType: BrandAsset["asset_type"], file: File) => void;
  onDelete?: (assetType: BrandAsset["asset_type"]) => void;
  uploading?: BrandAsset["asset_type"] | undefined;
  deleting?: BrandAsset["asset_type"] | undefined;
};

const BrandingCard: React.FC<BrandingCardProps> = ({
  initialBranding,
  onSave,
  assetByType = {},
  onUpload,
  onDelete,
  uploading,
  deleting,
}) => {
  const [branding, setBranding] = useState<BrandingData>(initialBranding);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLogoUrl, setShowLogoUrl] = useState(false);
  const [showCoverUrl, setShowCoverUrl] = useState(false);

  useEffect(() => {
    setBranding(initialBranding);
  }, [initialBranding]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // 🔥 Remove undefined values before sending to API
      const cleaned = Object.fromEntries(
        Object.entries(branding).filter(([_, v]) => v !== undefined)
      ) as Partial<BrandingData>;

      await onSave(cleaned);
    } catch (err: any) {
      setError(err.message || "Could not save branding");
    } finally {
      setIsSaving(false);
    }
  };

  const logoAssetUrl = assetByType["logo"]?.public_url ?? null;
  const coverAssetUrl = assetByType["cover_image"]?.public_url ?? null;
  const mobileCoverAssetUrl = assetByType["cover_image_mobile"]?.public_url ?? null;

  const effectiveLogoUrl = logoAssetUrl ?? branding.logo_url;
  const effectiveCoverUrl = coverAssetUrl ?? branding.booking_background_image_url;

  return (
    <Card elevation={1}>
      <CardHeader title="Branding" />

      <CardContent>
        <Stack spacing={3}>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {/* Logo */}
          <Box>
            <BrandAssetUpload
              label="Logo"
              helperText="Displayed on your booking page. PNG, JPEG or WebP, max 5 MB."
              assetType="logo"
              currentUrl={effectiveLogoUrl}
              onUpload={(file) => onUpload?.("logo", file)}
              onDelete={() => onDelete?.("logo")}
              uploading={uploading === "logo"}
              deleting={deleting === "logo"}
            />
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={() => setShowLogoUrl((v) => !v)}
              >
                {showLogoUrl ? "Hide URL field" : "Or use a URL instead"}
              </Link>
              <Collapse in={showLogoUrl}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://logo..."
                    value={branding.logo_url || ""}
                    onChange={(e) =>
                      setBranding({ ...branding, logo_url: e.target.value })
                    }
                  />
                  {branding.logo_url && !logoAssetUrl && (
                    <Avatar
                      src={branding.logo_url}
                      variant="rounded"
                      sx={{ width: 48, height: 48 }}
                    />
                  )}
                </Stack>
              </Collapse>
            </Box>
          </Box>

          {/* Primary Color */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Primary Color
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <input
                type="color"
                value={branding.primary_colour || "#111827"}
                onChange={(e) =>
                  setBranding({ ...branding, primary_colour: e.target.value })
                }
                style={{
                  width: 40,
                  height: 40,
                  border: "none",
                  cursor: "pointer",
                }}
              />

              <TextField
                size="small"
                sx={{ width: 130 }}
                value={branding.primary_colour || ""}
                onChange={(e) =>
                  setBranding({ ...branding, primary_colour: e.target.value })
                }
              />
            </Stack>
          </Box>

          {/* Secondary Color */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Secondary Color
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <input
                type="color"
                value={branding.secondary_colour || "#f97316"}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    secondary_colour: e.target.value,
                  })
                }
                style={{
                  width: 40,
                  height: 40,
                  border: "none",
                  cursor: "pointer",
                }}
              />

              <TextField
                size="small"
                sx={{ width: 130 }}
                value={branding.secondary_colour || ""}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    secondary_colour: e.target.value,
                  })
                }
              />
            </Stack>
          </Box>

          {/* Booking background color */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Booking page background color
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <input
                type="color"
                value={branding.booking_background_colour || "#f9fafb"}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    booking_background_colour: e.target.value,
                  })
                }
                style={{
                  width: 40,
                  height: 40,
                  border: "none",
                  cursor: "pointer",
                }}
              />

              <TextField
                size="small"
                sx={{ width: 130 }}
                value={branding.booking_background_colour || ""}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    booking_background_colour: e.target.value,
                  })
                }
                placeholder="#f9fafb"
              />
            </Stack>
          </Box>

          {/* Cover Image */}
          <Box>
            <BrandAssetUpload
              label="Cover Image"
              helperText="Recommended: 1600×600 px or wider. PNG, JPEG or WebP, max 5 MB."
              assetType="cover_image"
              currentUrl={effectiveCoverUrl ?? null}
              onUpload={(file) => onUpload?.("cover_image", file)}
              onDelete={() => onDelete?.("cover_image")}
              uploading={uploading === "cover_image"}
              deleting={deleting === "cover_image"}
            />
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={() => setShowCoverUrl((v) => !v)}
              >
                {showCoverUrl ? "Hide URL field" : "Or use a URL instead"}
              </Link>
              <Collapse in={showCoverUrl}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://image..."
                    value={branding.booking_background_image_url || ""}
                    onChange={(e) =>
                      setBranding({
                        ...branding,
                        booking_background_image_url: e.target.value,
                      })
                    }
                  />
                  {branding.booking_background_image_url && !coverAssetUrl && (
                    <Avatar
                      src={branding.booking_background_image_url}
                      variant="rounded"
                      sx={{ width: 48, height: 48 }}
                    />
                  )}
                </Stack>
              </Collapse>
            </Box>
          </Box>

          {/* Mobile Cover Image */}
          <Box>
            <BrandAssetUpload
              label="Mobile Cover Image (optional)"
              helperText="Portrait-friendly image for phones (recommended: 900×1200 px). Falls back to cover image when absent."
              assetType="cover_image_mobile"
              currentUrl={mobileCoverAssetUrl}
              onUpload={(file) => onUpload?.("cover_image_mobile", file)}
              onDelete={() => onDelete?.("cover_image_mobile")}
              uploading={uploading === "cover_image_mobile"}
              deleting={deleting === "cover_image_mobile"}
            />
          </Box>

          {/* Save Button */}
          <Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Branding"}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BrandingCard;
